import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, FolderKanban, Plus, Loader2, FileCode, Hammer, MessageSquare, Image as ImageIcon, ChevronLeft, Trash2, Code2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ProjectsDrawer({ open, onClose }) {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newName, setNewName] = useState("");
  const [buildPrompt, setBuildPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewFile, setViewFile] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/projects"); setProjects(data.projects); } catch {}
  }, []);
  const loadDetail = useCallback(async (id) => {
    try { const { data } = await api.get(`/projects/${id}`); setDetail(data); } catch {}
  }, []);

  useEffect(() => { if (open) { load(); setActive(null); setDetail(null); } }, [open, load]);
  useEffect(() => { if (active) loadDetail(active); }, [active, loadDetail]);

  if (!open) return null;

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/projects", { name: newName.trim() }); setNewName(""); await load(); setActive(data.project.id); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/projects/${id}`); if (active === id) setActive(null); load(); };
  const runBuild = async () => {
    if (!buildPrompt.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${active}/build`, { prompt: buildPrompt.trim() });
      toast.success(data.summary || "Build complete.");
      setBuildPrompt(""); loadDetail(active);
    } catch { toast.error("Build failed — try again."); }
    finally { setBusy(false); }
  };
  const openFile = async (fid) => {
    const { data } = await api.get(`/projects/${active}/files/${fid}`); setViewFile(data.file);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside data-testid="projects-drawer" className="fixed z-50 top-0 right-0 h-full w-full sm:w-[520px] glass-panel border-l border-white/10 flex flex-col fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            {active && <button data-testid="projects-back" onClick={() => { setActive(null); setDetail(null); }} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>}
            <div>
              <div className="label-mono">Build</div>
              <h2 className="font-heading text-lg font-medium mt-0.5 flex items-center gap-2"><FolderKanban className="w-4 h-4 text-mono-300" /> {detail?.project?.name || "My projects"}</h2>
            </div>
          </div>
          <button data-testid="projects-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!active && (
          <div className="p-4 flex flex-col gap-3 overflow-y-auto thin-scroll flex-1">
            <div className="reading text-sm text-slate-400" data-testid="build-intro">Build turns a conversation into real code files. Pick a project below or start a new one, then describe what to build.</div>
            <div className="label-mono flex items-center gap-1 mt-1" data-testid="my-projects-heading"><FolderKanban className="w-3.5 h-3.5" /> My projects</div>
            <div className="flex gap-2">
              <input data-testid="project-name-input" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="New project name…" onKeyDown={(e) => e.key === "Enter" && create()}
                className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
              <button data-testid="project-create" onClick={create} disabled={busy || !newName.trim()}
                className="rounded-full px-4 py-2 text-sm text-[#000000] font-medium flex items-center gap-1 disabled:opacity-50" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
            {projects.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No projects yet. Create one to organize chats, media and built files.</div>}
            {projects.map((p) => (
              <div key={p.id} data-testid="project-item" onClick={() => setActive(p.id)}
                className="group rounded-xl border border-white/10 px-4 py-3 cursor-pointer hover:border-mono-400/40 transition-all flex items-center justify-between">
                <div><div className="text-sm font-medium">{p.name}</div><div className="text-xs text-slate-500">{p.description || "—"}</div></div>
                <button data-testid="project-delete" onClick={(e) => { e.stopPropagation(); del(p.id); }} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {active && detail && (
          <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-5">
            <div>
              <div className="label-mono flex items-center gap-1 mb-2"><Hammer className="w-3.5 h-3.5" /> Build</div>
              <textarea data-testid="build-prompt" value={buildPrompt} onChange={(e) => setBuildPrompt(e.target.value)} rows={3}
                placeholder="Describe what to build or change… e.g. 'Add a contact form to index.html'"
                className="w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50 resize-none" />
              <button data-testid="build-run" onClick={runBuild} disabled={busy || !buildPrompt.trim()}
                className="mt-2 rounded-full px-5 py-2 text-sm text-[#000000] font-medium flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />} Build
              </button>
            </div>

            <div>
              <div className="label-mono flex items-center gap-1 mb-2"><FileCode className="w-3.5 h-3.5" /> Files ({detail.files.length})</div>
              <button data-testid="open-in-stealth-code" onClick={() => { window.history.pushState(null, "", `/?code=${active}`); window.dispatchEvent(new PopStateEvent("popstate")); }}
                className="mb-2 w-full rounded-lg px-3 py-2 border border-white/15 hover:bg-white/5 text-left text-sm text-slate-200 flex items-center gap-2">
                <Code2 className="w-3.5 h-3.5" /> Open in Stealth Code
              </button>
              {detail.files.length === 0 && <div className="text-xs text-slate-500">No files yet — describe something to build above.</div>}
              <div className="space-y-1" data-testid="project-files">
                {detail.files.map((f) => (
                  <button key={f.id} data-testid="project-file" onClick={() => openFile(f.id)}
                    className="w-full flex items-center justify-between rounded-lg px-3 py-2 bg-white/5 hover:bg-white/10 text-left">
                    <span className="text-sm text-slate-200 flex items-center gap-2"><FileCode className="w-3.5 h-3.5 text-mono-300" /> {f.path}</span>
                    <span className="text-[11px] text-slate-500">{f.size} chars</span>
                  </button>
                ))}
              </div>
            </div>

            {detail.media.length > 0 && (
              <div>
                <div className="label-mono flex items-center gap-1 mb-2"><ImageIcon className="w-3.5 h-3.5" /> Media</div>
                <div className="grid grid-cols-3 gap-2">
                  {detail.media.map((m) => m.kind === "image"
                    ? <img key={m.id} src={`${BACKEND}${m.url}`} alt={m.prompt} className="rounded-lg border border-white/10 object-cover h-20 w-full" />
                    : <video key={m.id} src={`${BACKEND}${m.url}`} className="rounded-lg border border-white/10 h-20 w-full" />)}
                </div>
              </div>
            )}

            <div>
              <div className="label-mono flex items-center gap-1 mb-2"><MessageSquare className="w-3.5 h-3.5" /> Conversations ({detail.conversations.length})</div>
              {detail.conversations.length === 0 && <div className="text-xs text-slate-500">No linked conversations.</div>}
              {detail.conversations.map((c) => (
                <div key={c.id} className="rounded-lg px-3 py-2 bg-white/5 text-sm text-slate-200 mb-1">{c.title}</div>
              ))}
            </div>
          </div>
        )}
      </aside>

      {viewFile && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70" onClick={() => setViewFile(null)}>
          <div className="w-full max-w-2xl glass-panel rounded-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="text-sm font-medium flex items-center gap-2"><FileCode className="w-4 h-4 text-mono-300" /> {viewFile.path}</span>
              <button onClick={() => setViewFile(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <pre className="p-4 overflow-auto thin-scroll text-xs text-slate-200 whitespace-pre-wrap" data-testid="file-content">{viewFile.content}</pre>
          </div>
        </div>
      )}
    </>
  );
}
